import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, MapPin, Search, Scale, FileText, User, Building2, Phone, Mail, AlertCircle, FileSignature } from 'lucide-react';
import { Button } from '../components/ui/button';
import SignaturePad from '../components/SignaturePad';
import { generateAbtretungserklaerungPDF, generateVollmachtPDF, pdfToBlob } from '../utils/pdfGenerator';

// Tire Data Cache
let tireDataCache = null;

// Hook to load tire data
const useTireData = () => {
  const [tireData, setTireData] = useState(tireDataCache);
  const [loading, setLoading] = useState(!tireDataCache);

  useEffect(() => {
    if (tireDataCache) {
      setTireData(tireDataCache);
      return;
    }

    const loadTireData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tires/complete`);
        const data = await response.json();
        tireDataCache = data;
        setTireData(data);
      } catch (error) {
        console.error('Failed to load tire data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTireData();
  }, []);

  return { tireData, loading };
};

// Google Places Autocomplete Hook
const useGooglePlacesAutocomplete = (inputRef, onPlaceSelect) => {
  const autocompleteRef = useRef(null);
  
  useEffect(() => {
    if (!inputRef.current || !window.google?.maps?.places) return;
    
    autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'de' },
      fields: ['address_components', 'formatted_address', 'geometry'],
      types: ['address']
    });
    
    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      if (place && onPlaceSelect) {
        const addressComponents = place.address_components || [];
        const getComponent = (type) => addressComponents.find(c => c.types.includes(type))?.long_name || '';
        
        onPlaceSelect({
          street: `${getComponent('route')} ${getComponent('street_number')}`.trim(),
          city: getComponent('locality') || getComponent('administrative_area_level_2'),
          zip: getComponent('postal_code'),
          formatted: place.formatted_address
        });
      }
    });
    
    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [inputRef, onPlaceSelect]);
};

// Load Google Maps Script
const loadGoogleMapsScript = (callback) => {
  if (window.google?.maps?.places) {
    callback();
    return;
  }
  
  const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
  if (existingScript) {
    existingScript.addEventListener('load', callback);
    return;
  }
  
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_PLACES_KEY || 'AIzaSyCXBJn6174IR4S2eQt4gbT3E0d6JQXgihA'}&libraries=places&language=de`;
  script.async = true;
  script.defer = true;
  script.onload = callback;
  document.head.appendChild(script);
};

const NeuerFallPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [caseId, setCaseId] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreementMissing, setAgreementMissing] = useState(false);

  const [step1Data, setStep1Data] = useState({
    license_plate: '',
    damage_date: '',
    damage_street: '',
    damage_zip: '',
    damage_city: '',
    damage_location: '', // Combined for backward compatibility
    insurance_number: '',
    claim_number: '',
    opposing_license_plate: '',
    victim_name: '',
    victim_phone: '',
    victim_email: '',
    victim_street: '',
    victim_zip: '',
    victim_city: '',
    // Schädiger-Daten
    schaediger_name: '',
    schaediger_strasse: '',
    schaediger_plz: '',
    schaediger_ort: '',
    schaediger_kennzeichen: '',
    schaediger_versicherung: '',
    schaediger_vers_nummer: '',
    schaediger_schadennummer: ''
  });

  const [step2Data, setStep2Data] = useState({
    vehicle_manufacturer: '',
    vehicle_model: '',
    vehicle_mileage: '',
    // Neue Fahrzeugdaten
    first_registration_type: '', // 'keine' or 'date'
    first_registration_date: '',
    next_hu: '', // Format: MM.YYYY
    mileage_unit: 'Kilometer', // Kilometer or Meilen
    previous_owners: '',
    // Reifen Vorderachse
    tire_dimension_front: '',
    tire_manufacturer_front: '',
    tire_profile_front_mm: '',
    // Reifen Hinterachse
    tire_dimension_rear: '',
    tire_manufacturer_rear: '',
    tire_profile_rear_mm: '',
    // Gemeinsame Reifendaten
    tire_season: '', // Sommer, Winter, Ganzjahres
    rim_type: '', // Alu, Stahl
    // Legacy fields for backward compatibility
    tire_dimension: '',
    tire_profile_left_mm: '',
    tire_profile_right_mm: '',
    // Schaden
    damage_description: '',
    vehicle_drivable: null,
    pre_damage: '',
    pre_damage_description: ''
  });

  // Step 3: Legal Documents
  const [step3Data, setStep3Data] = useState({
    // Anwalt
    lawyer_option: '', // 'none', 'own', 'cooperating'
    own_lawyer_name: '',
    own_lawyer_kanzlei: '',
    own_lawyer_phone: '',
    own_lawyer_email: '',
    own_lawyer_strasse: '',
    own_lawyer_plz: '',
    own_lawyer_ort: '',
    // Abtretungserklärung
    abtretung_accept_agb: false,
    abtretung_accept_widerruf: false,
    abtretung_accept_sofort: false,
    abtretung_signature: null,
    // Vollmacht
    vollmacht_accept_dsgvo: false,
    vollmacht_signature: null
  });

  // Load state from localStorage
  useEffect(() => {
    const savedStep1 = localStorage.getItem(`case_step1_${caseId}`);
    const savedStep2 = localStorage.getItem(`case_step2_${caseId}`);
    const savedStep3 = localStorage.getItem(`case_step3_${caseId}`);
    const savedCurrentStep = localStorage.getItem(`case_current_step_${caseId}`);
    
    if (savedStep1) {
      try {
        setStep1Data(JSON.parse(savedStep1));
      } catch (e) {}
    }
    
    if (savedStep2) {
      try {
        setStep2Data(JSON.parse(savedStep2));
      } catch (e) {}
    }
    
    if (savedStep3) {
      try {
        // Don't restore signatures from localStorage (they're large)
        const parsed = JSON.parse(savedStep3);
        setStep3Data({
          ...parsed,
          abtretung_signature: null,
          vollmacht_signature: null
        });
      } catch (e) {}
    }
    
    if (savedCurrentStep) {
      setCurrentStep(parseInt(savedCurrentStep));
    }
  }, [caseId]);

  // Save state to localStorage when data changes
  useEffect(() => {
    if (caseId) {
      localStorage.setItem(`case_step1_${caseId}`, JSON.stringify(step1Data));
    }
  }, [step1Data, caseId]);

  useEffect(() => {
    if (caseId) {
      localStorage.setItem(`case_step2_${caseId}`, JSON.stringify(step2Data));
    }
  }, [step2Data, caseId]);

  useEffect(() => {
    if (caseId) {
      // Don't save signatures to localStorage (too large)
      const dataToSave = {
        ...step3Data,
        abtretung_signature: null,
        vollmacht_signature: null
      };
      localStorage.setItem(`case_step3_${caseId}`, JSON.stringify(dataToSave));
    }
  }, [step3Data, caseId]);

  useEffect(() => {
    if (caseId) {
      localStorage.setItem(`case_current_step_${caseId}`, currentStep.toString());
    }
  }, [currentStep, caseId]);

  useEffect(() => {
    const pid = searchParams.get('partner_id') || localStorage.getItem('partner_id');
    const cid = searchParams.get('case_id');
    
    if (!pid) {
      navigate('/partner/registrieren');
      return;
    }
    
    setPartnerId(pid);
    
    // Check if partner has signed agreement
    checkAgreementStatus(pid);
    
    if (cid) {
      setCaseId(cid);
    } else {
      createCase(pid);
    }
  }, [searchParams, navigate]);

  const checkAgreementStatus = async (pid) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/partner-agreement/status/${pid}`
      );
      if (response.ok) {
        const data = await response.json();
        if (!data.has_signed) {
          setAgreementMissing(true);
        }
      }
    } catch (error) {
      console.error('Agreement check failed:', error);
    }
  };

  const createCase = async (pid) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cases/create?partner_id=${pid}`,
        { method: 'POST' }
      );
      const data = await response.json();
      setCaseId(data.case_id);
    } catch (error) {
      console.error('Create case failed:', error);
    }
  };

  const saveStep = async (step, data) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cases/step${step}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ case_id: caseId, ...data })
        }
      );

      if (!response.ok) throw new Error('Speichern fehlgeschlagen');
      return true;
    } catch (err) {
      alert(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleStep1Next = async () => {
    // Block if agreement not signed
    if (agreementMissing) {
      alert('Bitte unterschreiben Sie zuerst die Partnervereinbarung.');
      return;
    }
    
    if (!step1Data.license_plate || !step1Data.damage_date || !step1Data.damage_street || !step1Data.damage_zip || !step1Data.damage_city) {
      alert('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    // Combine address for backward compatibility
    const dataToSave = {
      ...step1Data,
      damage_location: `${step1Data.damage_street}, ${step1Data.damage_zip} ${step1Data.damage_city}`
    };
    const success = await saveStep(1, dataToSave);
    if (success) setCurrentStep(2);
  };

  const handleStep2Next = async () => {
    if (!step2Data.vehicle_manufacturer || !step2Data.vehicle_model || !step2Data.vehicle_mileage ||
        !step2Data.tire_dimension_front || !step2Data.tire_dimension_rear ||
        !step2Data.tire_manufacturer_front || !step2Data.tire_manufacturer_rear ||
        !step2Data.tire_profile_front_mm || !step2Data.tire_profile_rear_mm ||
        !step2Data.tire_season || !step2Data.rim_type ||
        !step2Data.damage_description || !step2Data.damage_zone || step2Data.vehicle_drivable === null ||
        !step2Data.pre_damage) {
      alert('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    
    if (step2Data.pre_damage === 'Ja' && !step2Data.pre_damage_description) {
      alert('Bitte Vorschäden beschreiben');
      return;
    }
    
    // Map new fields to legacy format for backward compatibility
    const dataToSave = {
      ...step2Data,
      vehicle_mileage: parseInt(step2Data.vehicle_mileage),
      // Neue Fahrzeugfelder
      first_registration_type: step2Data.first_registration_type,
      first_registration_date: step2Data.first_registration_date,
      next_hu: step2Data.next_hu,
      mileage_unit: step2Data.mileage_unit || 'Kilometer',
      previous_owners: step2Data.previous_owners,
      // Reifen
      tire_dimension: `VA: ${step2Data.tire_dimension_front} / HA: ${step2Data.tire_dimension_rear}`,
      tire_profile_left_mm: parseFloat(step2Data.tire_profile_front_mm),
      tire_profile_right_mm: parseFloat(step2Data.tire_profile_rear_mm),
      tire_profile_front_mm: parseFloat(step2Data.tire_profile_front_mm),
      tire_profile_rear_mm: parseFloat(step2Data.tire_profile_rear_mm)
    };
    
    const success = await saveStep(2, dataToSave);
    
    if (success) {
      setCurrentStep(3); // Go to legal documents step
    }
  };

  // Step 3: Save Legal Documents and Generate PDFs
  const handleStep3Next = async () => {
    // Validate Abtretungserklärung
    if (!step3Data.abtretung_accept_agb || !step3Data.abtretung_accept_widerruf || !step3Data.abtretung_accept_sofort) {
      alert('Bitte alle Checkboxen der Abtretungserklärung bestätigen');
      return;
    }
    
    if (!step3Data.abtretung_signature) {
      alert('Bitte die Abtretungserklärung unterschreiben');
      return;
    }
    
    // Validate lawyer choice
    if (!step3Data.lawyer_option) {
      alert('Bitte wählen Sie eine Anwalt-Option');
      return;
    }
    
    // Validate own lawyer data if selected
    if (step3Data.lawyer_option === 'own') {
      if (!step3Data.own_lawyer_name || !step3Data.own_lawyer_phone || !step3Data.own_lawyer_email) {
        alert('Bitte geben Sie die Anwaltsdaten ein');
        return;
      }
    }
    
    // Validate Vollmacht if lawyer is needed
    if (step3Data.lawyer_option !== 'none') {
      if (!step3Data.vollmacht_accept_dsgvo) {
        alert('Bitte bestätigen Sie die Datenschutzhinweise für die Vollmacht');
        return;
      }
      if (!step3Data.vollmacht_signature) {
        alert('Bitte unterschreiben Sie die Vollmacht');
        return;
      }
    }
    
    setLoading(true);
    
    try {
      // Generate Abtretungserklärung PDF
      const abtretungData = {
        aktenzeichen: caseId.substring(0, 8).toUpperCase(),
        schadentag: step1Data.damage_date,
        schadenort: `${step1Data.damage_street}, ${step1Data.damage_zip} ${step1Data.damage_city}`,
        anspruchsteller_name: step1Data.victim_name,
        anspruchsteller_strasse: step1Data.victim_street || step1Data.damage_street,
        anspruchsteller_plz: step1Data.victim_zip || step1Data.damage_zip,
        anspruchsteller_ort: step1Data.victim_city || step1Data.damage_city,
        anspruchsteller_kennzeichen: step1Data.license_plate,
        anspruchsteller_telefon: step1Data.victim_phone,
        anspruchsteller_email: step1Data.victim_email,
        schaediger_name: step1Data.schaediger_name,
        schaediger_strasse: step1Data.schaediger_strasse,
        schaediger_plz: step1Data.schaediger_plz,
        schaediger_ort: step1Data.schaediger_ort,
        schaediger_kennzeichen: step1Data.schaediger_kennzeichen || step1Data.opposing_license_plate,
        schaediger_versicherung: step1Data.schaediger_versicherung,
        schaediger_vers_nummer: step1Data.schaediger_vers_nummer || step1Data.insurance_number,
        schaediger_schadennummer: step1Data.schaediger_schadennummer || step1Data.claim_number,
        accept_agb: step3Data.abtretung_accept_agb,
        accept_widerruf: step3Data.abtretung_accept_widerruf,
        accept_sofort: step3Data.abtretung_accept_sofort,
        signature: step3Data.abtretung_signature
      };
      
      const abtretungPdf = await generateAbtretungserklaerungPDF(abtretungData);
      const abtretungBlob = pdfToBlob(abtretungPdf);
      
      // Upload Abtretungserklärung PDF
      const abtretungFormData = new FormData();
      abtretungFormData.append('file', abtretungBlob, `Abtretungserklaerung_${caseId}.pdf`);
      abtretungFormData.append('case_id', caseId);
      abtretungFormData.append('document_type', 'abtretungserklaerung');
      
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cases/upload-legal-document`, {
        method: 'POST',
        body: abtretungFormData
      });
      
      // Generate and upload Vollmacht if lawyer needed
      if (step3Data.lawyer_option !== 'none') {
        const vollmachtData = {
          anwalt_name: step3Data.lawyer_option === 'own' ? step3Data.own_lawyer_name : null,
          anwalt_kanzlei: step3Data.lawyer_option === 'own' ? step3Data.own_lawyer_kanzlei : null,
          vollmachtgeber_name: step1Data.victim_name,
          vollmachtgeber_adresse: `${step1Data.victim_street || step1Data.damage_street}, ${step1Data.victim_zip || step1Data.damage_zip} ${step1Data.victim_city || step1Data.damage_city}`,
          schaediger_name: step1Data.schaediger_name,
          unfalldatum: step1Data.damage_date,
          accept_dsgvo: step3Data.vollmacht_accept_dsgvo,
          signature: step3Data.vollmacht_signature
        };
        
        const vollmachtPdf = await generateVollmachtPDF(vollmachtData);
        const vollmachtBlob = pdfToBlob(vollmachtPdf);
        
        const vollmachtFormData = new FormData();
        vollmachtFormData.append('file', vollmachtBlob, `Vollmacht_${caseId}.pdf`);
        vollmachtFormData.append('case_id', caseId);
        vollmachtFormData.append('document_type', 'vollmacht');
        
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cases/upload-legal-document`, {
          method: 'POST',
          body: vollmachtFormData
        });
      }
      
      // Save lawyer data to case
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cases/save-lawyer-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: caseId,
          lawyer_option: step3Data.lawyer_option,
          lawyer_name: step3Data.lawyer_option === 'own' ? step3Data.own_lawyer_name : null,
          lawyer_kanzlei: step3Data.lawyer_option === 'own' ? step3Data.own_lawyer_kanzlei : null,
          lawyer_phone: step3Data.lawyer_option === 'own' ? step3Data.own_lawyer_phone : null,
          lawyer_email: step3Data.lawyer_option === 'own' ? step3Data.own_lawyer_email : null,
          lawyer_address: step3Data.lawyer_option === 'own' 
            ? `${step3Data.own_lawyer_strasse}, ${step3Data.own_lawyer_plz} ${step3Data.own_lawyer_ort}` 
            : null
        })
      });
      
      // Clear localStorage after successful submission
      localStorage.removeItem(`case_step1_${caseId}`);
      localStorage.removeItem(`case_step2_${caseId}`);
      localStorage.removeItem(`case_step3_${caseId}`);
      localStorage.removeItem(`case_current_step_${caseId}`);
      
      navigate(`/portal/bilder-upload?case_id=${caseId}&partner_id=${partnerId}`);
    } catch (error) {
      console.error('Error saving legal documents:', error);
      alert('Fehler beim Speichern der Dokumente. Bitte erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  const STEPS = [
    { number: 1, title: 'Auftragsdaten' },
    { number: 2, title: 'Fahrzeug' },
    { number: 3, title: 'Dokumente' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-6">
        {/* Agreement Missing Banner */}
        {agreementMissing && (
          <div className="mb-8 bg-orange-50 border-2 border-orange-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-orange-100 rounded-xl flex-shrink-0">
                <FileSignature className="w-6 h-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-orange-900 text-lg mb-2">
                  Partnervereinbarung erforderlich
                </h3>
                <p className="text-orange-800 mb-4">
                  Bevor Sie Fälle einreichen können, müssen Sie unsere Partnervereinbarung unterschreiben. 
                  Diese regelt die Provisionskonditionen (30% vom Netto-Grundhonorar) und weitere wichtige Bedingungen.
                </p>
                <Button
                  onClick={() => navigate(`/partner/vereinbarung?partner_id=${partnerId}`)}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="sign-agreement-banner-btn"
                >
                  <FileSignature className="w-4 h-4 mr-2" />
                  Jetzt Vereinbarung unterschreiben
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">Neuer Fall</h1>
          
          <div className="flex items-center justify-center gap-4 mb-8">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg mb-2 ${
                    step.number < currentStep ? 'bg-brand-green text-white' :
                    step.number === currentStep ? 'bg-brand-green text-white ring-4 ring-brand-green/20' :
                    'bg-gray-200 text-gray-500'
                  }`}>
                    {step.number < currentStep ? <Check className="w-6 h-6" /> : step.number}
                  </div>
                  <span className="text-xs font-medium text-gray-600">{step.title}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 max-w-[100px] rounded ${step.number < currentStep ? 'bg-brand-green' : 'bg-gray-200'}`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="bg-white border-2 border-gray-100 rounded-3xl p-10 shadow-xl mb-20">
          {currentStep === 1 && (
            <Step1Content data={step1Data} setData={setStep1Data} onNext={handleStep1Next} loading={loading} />
          )}
          {currentStep === 2 && (
            <Step2Content data={step2Data} setData={setStep2Data} onNext={handleStep2Next} onBack={() => setCurrentStep(1)} loading={loading} />
          )}
          {currentStep === 3 && (
            <Step3Content 
              data={step3Data} 
              setData={setStep3Data} 
              step1Data={step1Data}
              onNext={handleStep3Next} 
              onBack={() => setCurrentStep(2)} 
              loading={loading} 
            />
          )}
        </div>
      </div>
    </div>
  );
};

const Step1Content = ({ data, setData, onNext, loading }) => {
  const [errors, setErrors] = React.useState({});
  const [googleLoaded, setGoogleLoaded] = React.useState(false);
  const streetInputRef = React.useRef(null);
  const victimStreetInputRef = React.useRef(null);
  const schaedigerStreetInputRef = React.useRef(null);

  // Load Google Maps on mount
  React.useEffect(() => {
    loadGoogleMapsScript(() => setGoogleLoaded(true));
  }, []);

  // Handle place selection from autocomplete - Schadenort
  const handlePlaceSelect = React.useCallback((place) => {
    setData(prev => ({
      ...prev,
      damage_street: place.street,
      damage_zip: place.zip,
      damage_city: place.city
    }));
    setErrors(prev => ({...prev, damage_street: null, damage_zip: null, damage_city: null}));
  }, [setData]);

  // Handle place selection - Geschädigter
  const handleVictimPlaceSelect = React.useCallback((place) => {
    setData(prev => ({
      ...prev,
      victim_street: place.street,
      victim_zip: place.zip,
      victim_city: place.city
    }));
  }, [setData]);

  // Handle place selection - Schädiger
  const handleSchaedigerPlaceSelect = React.useCallback((place) => {
    setData(prev => ({
      ...prev,
      schaediger_strasse: place.street,
      schaediger_plz: place.zip,
      schaediger_ort: place.city
    }));
  }, [setData]);

  // Initialize autocomplete when Google is loaded
  useGooglePlacesAutocomplete(streetInputRef, handlePlaceSelect);
  useGooglePlacesAutocomplete(victimStreetInputRef, handleVictimPlaceSelect);
  useGooglePlacesAutocomplete(schaedigerStreetInputRef, handleSchaedigerPlaceSelect);

  const validateKennzeichen = (value) => {
    // German license plate format: XX AB 1234 or XX-AB-1234
    const pattern = /^[A-ZÄÖÜ]{1,3}[\s-]?[A-ZÄÖÜ]{1,2}[\s-]?\d{1,4}[EH]?$/i;
    return pattern.test(value.trim());
  };

  const handleNext = () => {
    const newErrors = {};
    
    if (!data.license_plate || !validateKennzeichen(data.license_plate)) {
      newErrors.license_plate = 'Bitte gültiges Kennzeichen eingeben (z.B. HH AB 1234)';
    }
    if (!data.damage_date) {
      newErrors.damage_date = 'Bitte Schadentag angeben';
    }
    if (!data.damage_street) {
      newErrors.damage_street = 'Bitte Straße angeben';
    }
    if (!data.damage_zip) {
      newErrors.damage_zip = 'Bitte PLZ angeben';
    }
    if (!data.damage_city) {
      newErrors.damage_city = 'Bitte Stadt angeben';
    }
    // Geschädigten-Daten
    if (!data.victim_name) {
      newErrors.victim_name = 'Bitte Name des Geschädigten angeben';
    }
    if (!data.victim_phone) {
      newErrors.victim_phone = 'Bitte Telefonnummer angeben';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onNext();
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Auftragsdaten</h2>
        <p className="text-sm text-gray-500 mb-2">Grundlegende Informationen zum Schadenfall</p>
        <p className="text-xs text-brand-green font-medium">⏱ Dauer: ca. 3-4 Minuten</p>
      </div>

      {/* Unfalldaten */}
      <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
          <MapPin className="w-5 h-5 mr-2 text-brand-green" />
          Unfalldaten
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Kennzeichen des Geschädigten *</label>
            <input 
              type="text" 
              required 
              value={data.license_plate} 
              onChange={(e) => {
                setData({ ...data, license_plate: e.target.value.toUpperCase() });
                if (errors.license_plate) setErrors({...errors, license_plate: null});
              }}
              className={`w-full px-4 py-3 border-2 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition-all ${errors.license_plate ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}
              placeholder="z.B. HH AB 1234" 
              data-testid="license-plate-input"
            />
            {errors.license_plate && (
              <p className="text-xs text-red-600 mt-1">{errors.license_plate}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Schadentag *</label>
            <input 
              type="date" 
              required 
              value={data.damage_date} 
              onChange={(e) => {
                setData({ ...data, damage_date: e.target.value });
                if (errors.damage_date) setErrors({...errors, damage_date: null});
              }}
              className={`w-full px-4 py-3 border-2 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition-all ${errors.damage_date ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}
              data-testid="damage-date-input"
            />
            {errors.damage_date && (
              <p className="text-xs text-red-600 mt-1">{errors.damage_date}</p>
            )}
          </div>
          
          {/* Schadenort mit Google Maps Autocomplete */}
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Schadenort - Straße *
            </label>
            <input 
              ref={streetInputRef}
              type="text" 
              required 
              value={data.damage_street} 
              onChange={(e) => {
                setData({ ...data, damage_street: e.target.value });
                if (errors.damage_street) setErrors({...errors, damage_street: null});
              }}
              className={`w-full px-4 py-3 border-2 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition-all ${errors.damage_street ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}
              placeholder="Straße eingeben... (Google Maps Vorschläge)" 
              data-testid="damage-street-input"
            />
            {errors.damage_street && (
              <p className="text-xs text-red-600 mt-1">{errors.damage_street}</p>
            )}
            {googleLoaded && (
              <p className="text-xs text-gray-500 mt-1">💡 Tippen Sie eine Adresse ein für automatische Vervollständigung</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">PLZ *</label>
            <input 
              type="text" 
              required 
              value={data.damage_zip} 
              onChange={(e) => {
                setData({ ...data, damage_zip: e.target.value });
                if (errors.damage_zip) setErrors({...errors, damage_zip: null});
              }}
              className={`w-full px-4 py-3 border-2 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition-all ${errors.damage_zip ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}
              placeholder="z.B. 20095" 
              data-testid="damage-zip-input"
            />
            {errors.damage_zip && (
              <p className="text-xs text-red-600 mt-1">{errors.damage_zip}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Stadt *</label>
            <input 
              type="text" 
              required 
              value={data.damage_city} 
              onChange={(e) => {
                setData({ ...data, damage_city: e.target.value });
                if (errors.damage_city) setErrors({...errors, damage_city: null});
              }}
              className={`w-full px-4 py-3 border-2 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition-all ${errors.damage_city ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}
              placeholder="z.B. Hamburg" 
              data-testid="damage-city-input"
            />
            {errors.damage_city && (
              <p className="text-xs text-red-600 mt-1">{errors.damage_city}</p>
            )}
          </div>
        </div>
      </div>

      {/* Anspruchsteller / Geschädigter */}
      <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
          <User className="w-5 h-5 mr-2 text-blue-600" />
          Anspruchsteller / Geschädigter
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Name *</label>
            <input 
              type="text" 
              required 
              value={data.victim_name} 
              onChange={(e) => {
                setData({ ...data, victim_name: e.target.value });
                if (errors.victim_name) setErrors({...errors, victim_name: null});
              }}
              className={`w-full px-4 py-3 border-2 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500 transition-all ${errors.victim_name ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}
              placeholder="Vollständiger Name" 
              data-testid="victim-name-input"
            />
            {errors.victim_name && (
              <p className="text-xs text-red-600 mt-1">{errors.victim_name}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Telefon / Mobil *</label>
            <input 
              type="tel" 
              required 
              value={data.victim_phone} 
              onChange={(e) => {
                setData({ ...data, victim_phone: e.target.value });
                if (errors.victim_phone) setErrors({...errors, victim_phone: null});
              }}
              className={`w-full px-4 py-3 border-2 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500 transition-all ${errors.victim_phone ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}
              placeholder="z.B. 0151 12345678" 
              data-testid="victim-phone-input"
            />
            {errors.victim_phone && (
              <p className="text-xs text-red-600 mt-1">{errors.victim_phone}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">E-Mail <span className="text-gray-400 font-normal">(optional)</span></label>
            <input 
              type="email" 
              value={data.victim_email} 
              onChange={(e) => setData({ ...data, victim_email: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 bg-white rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500 transition-all"
              placeholder="email@beispiel.de" 
              data-testid="victim-email-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Straße, Nr <span className="text-gray-400 font-normal">(optional)</span></label>
            <input 
              ref={victimStreetInputRef}
              type="text" 
              value={data.victim_street} 
              onChange={(e) => setData({ ...data, victim_street: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 bg-white rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500 transition-all"
              placeholder="Musterstraße 123" 
              data-testid="victim-street-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">PLZ <span className="text-gray-400 font-normal">(optional)</span></label>
            <input 
              type="text" 
              value={data.victim_zip} 
              onChange={(e) => setData({ ...data, victim_zip: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 bg-white rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500 transition-all"
              placeholder="20095" 
              data-testid="victim-zip-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Ort <span className="text-gray-400 font-normal">(optional)</span></label>
            <input 
              type="text" 
              value={data.victim_city} 
              onChange={(e) => setData({ ...data, victim_city: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 bg-white rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500 transition-all"
              placeholder="Hamburg" 
              data-testid="victim-city-input"
            />
          </div>
        </div>
      </div>

      {/* Schädiger / Unfallgegner */}
      <div className="bg-orange-50 rounded-2xl p-6 border border-orange-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2 text-orange-600" />
          Schädiger / Unfallgegner
        </h3>
        <p className="text-sm text-gray-600 mb-4">Diese Angaben sind optional, werden aber für die Abtretungserklärung benötigt.</p>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Name <span className="text-gray-400 font-normal">(optional)</span></label>
            <input 
              type="text" 
              value={data.schaediger_name} 
              onChange={(e) => setData({ ...data, schaediger_name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 bg-white rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-500 transition-all"
              placeholder="Name des Schädigers" 
              data-testid="schaediger-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Amtl. Kennzeichen <span className="text-gray-400 font-normal">(optional)</span></label>
            <input 
              type="text" 
              value={data.schaediger_kennzeichen || data.opposing_license_plate} 
              onChange={(e) => setData({ ...data, schaediger_kennzeichen: e.target.value.toUpperCase(), opposing_license_plate: e.target.value.toUpperCase() })}
              className="w-full px-4 py-3 border-2 border-gray-200 bg-white rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-500 transition-all"
              placeholder="z.B. HH XY 5678" 
              data-testid="schaediger-kennzeichen-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Straße, Nr <span className="text-gray-400 font-normal">(optional)</span></label>
            <input 
              ref={schaedigerStreetInputRef}
              type="text" 
              value={data.schaediger_strasse} 
              onChange={(e) => setData({ ...data, schaediger_strasse: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 bg-white rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-500 transition-all"
              placeholder="Musterstraße 123" 
              data-testid="schaediger-strasse-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">PLZ <span className="text-gray-400 font-normal">(optional)</span></label>
            <input 
              type="text" 
              value={data.schaediger_plz} 
              onChange={(e) => setData({ ...data, schaediger_plz: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 bg-white rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-500 transition-all"
              placeholder="20095" 
              data-testid="schaediger-plz-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Ort <span className="text-gray-400 font-normal">(optional)</span></label>
            <input 
              type="text" 
              value={data.schaediger_ort} 
              onChange={(e) => setData({ ...data, schaediger_ort: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 bg-white rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-500 transition-all"
              placeholder="Hamburg" 
              data-testid="schaediger-ort-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Versicherung <span className="text-gray-400 font-normal">(optional)</span></label>
            <input 
              type="text" 
              value={data.schaediger_versicherung} 
              onChange={(e) => setData({ ...data, schaediger_versicherung: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 bg-white rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-500 transition-all"
              placeholder="z.B. Allianz, HUK-Coburg" 
              data-testid="schaediger-versicherung-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Versicherungs-Nr <span className="text-gray-400 font-normal">(optional)</span></label>
            <input 
              type="text" 
              value={data.schaediger_vers_nummer || data.insurance_number} 
              onChange={(e) => setData({ ...data, schaediger_vers_nummer: e.target.value, insurance_number: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 bg-white rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-500 transition-all"
              placeholder="Versicherungsnummer" 
              data-testid="schaediger-vers-nummer-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Schadennummer <span className="text-gray-400 font-normal">(optional)</span></label>
            <input 
              type="text" 
              value={data.schaediger_schadennummer || data.claim_number} 
              onChange={(e) => setData({ ...data, schaediger_schadennummer: e.target.value, claim_number: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 bg-white rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-500 transition-all"
              placeholder="Schadennummer der Versicherung" 
              data-testid="schaediger-schadennummer-input"
            />
          </div>
        </div>
      </div>

    <div className="flex justify-end pt-6">
      <button onClick={handleNext} disabled={loading} className="bg-brand-green hover:bg-brand-green-dark text-white px-8 py-4 rounded-xl font-semibold inline-flex items-center gap-2 transition-colors disabled:opacity-50" data-testid="step1-next-btn">
        {loading ? 'Speichern...' : 'Weiter'}
        <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  </div>
  );
};

const Step2Content = ({ data, setData, onNext, onBack, loading }) => {
  const { tireData } = useTireData();
  const [dimensionSearch, setDimensionSearch] = React.useState({ front: '', rear: '' });
  const [filteredDimensions, setFilteredDimensions] = React.useState({ front: [], rear: [] });
  const [showDropdown, setShowDropdown] = React.useState({ front: false, rear: false });

  // Filter dimensions based on search
  const filterDimensions = (search, axis) => {
    if (!tireData?.dimensions || search.length < 2) {
      setFilteredDimensions(prev => ({ ...prev, [axis]: [] }));
      return;
    }
    const searchUpper = search.toUpperCase().replace(/\s/g, '');
    const filtered = tireData.dimensions.filter(d => 
      d.replace(/\s/g, '').includes(searchUpper)
    ).slice(0, 10);
    setFilteredDimensions(prev => ({ ...prev, [axis]: filtered }));
  };

  const selectDimension = (dimension, axis) => {
    if (axis === 'front') {
      setData({ ...data, tire_dimension_front: dimension });
      setDimensionSearch(prev => ({ ...prev, front: '' }));
    } else {
      setData({ ...data, tire_dimension_rear: dimension });
      setDimensionSearch(prev => ({ ...prev, rear: '' }));
    }
    setShowDropdown(prev => ({ ...prev, [axis]: false }));
  };

  return (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Fahrzeugdaten</h2>
      <p className="text-gray-600">Fahrzeug, Reifen, Schaden, Vorschäden</p>
    </div>

    {/* Fahrzeugdaten */}
    <div className="grid md:grid-cols-3 gap-6">
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">Hersteller *</label>
        <input type="text" required value={data.vehicle_manufacturer} onChange={(e) => setData({ ...data, vehicle_manufacturer: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green" placeholder="VW" data-testid="vehicle-manufacturer-input" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">Modell *</label>
        <input type="text" required value={data.vehicle_model} onChange={(e) => setData({ ...data, vehicle_model: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green" placeholder="Golf" data-testid="vehicle-model-input" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">Anz. Vorbesitzer <span className="text-gray-400 font-normal">(optional)</span></label>
        <input 
          type="number" 
          min="0"
          value={data.previous_owners} 
          onChange={(e) => setData({ ...data, previous_owners: e.target.value })} 
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green" 
          placeholder="z.B. 2" 
          data-testid="previous-owners-input" 
        />
      </div>
    </div>

    {/* Erstzulassung, HU, Laufleistung */}
    <div className="grid md:grid-cols-2 gap-6">
      {/* Erstzulassung */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Erstzulassung 
          <span className="ml-1 text-blue-500 cursor-help" title="Datum der ersten Zulassung des Fahrzeugs">ⓘ</span>
        </label>
        <div className="flex gap-2">
          <select 
            value={data.first_registration_type || 'keine'} 
            onChange={(e) => setData({ ...data, first_registration_type: e.target.value, first_registration_date: e.target.value === 'keine' ? '' : data.first_registration_date })} 
            className="px-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green bg-white"
            data-testid="first-registration-type-select"
          >
            <option value="keine">keine</option>
            <option value="date">Datum</option>
          </select>
          {data.first_registration_type === 'date' && (
            <input 
              type="date" 
              value={data.first_registration_date} 
              onChange={(e) => setData({ ...data, first_registration_date: e.target.value })} 
              className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green" 
              placeholder="z.B. 31.12.2001"
              data-testid="first-registration-date-input"
            />
          )}
        </div>
      </div>

      {/* Nächste HU */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">Nächste HU <span className="text-gray-400 font-normal">(optional)</span></label>
        <input 
          type="month" 
          value={data.next_hu} 
          onChange={(e) => setData({ ...data, next_hu: e.target.value })} 
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green" 
          placeholder="z.B. 12.2025"
          data-testid="next-hu-input"
        />
      </div>
    </div>

    {/* Laufleistung */}
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">Laufleistung *</label>
        <div className="flex gap-2">
          <input 
            type="number" 
            required 
            value={data.vehicle_mileage} 
            onChange={(e) => setData({ ...data, vehicle_mileage: e.target.value })} 
            className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green" 
            placeholder="50000" 
            data-testid="vehicle-mileage-input" 
          />
          <select 
            value={data.mileage_unit || 'Kilometer'} 
            onChange={(e) => setData({ ...data, mileage_unit: e.target.value })} 
            className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green bg-white"
            data-testid="mileage-unit-select"
          >
            <option value="Kilometer">Kilometer</option>
            <option value="Meilen">Meilen</option>
          </select>
        </div>
      </div>
    </div>

    {/* Reifendaten - Gemeinsame Eigenschaften */}
    <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
      <h3 className="text-lg font-bold text-gray-900 mb-4">🛞 Reifendaten</h3>
      
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">Reifenart / Jahreszeit *</label>
          <select 
            required 
            value={data.tire_season} 
            onChange={(e) => setData({ ...data, tire_season: e.target.value })} 
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green bg-white"
            data-testid="tire-season-select"
          >
            <option value="">Bitte wählen</option>
            <option value="Sommerreifen">Sommerreifen</option>
            <option value="Winterreifen">Winterreifen</option>
            <option value="Ganzjahresreifen">Ganzjahresreifen</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">Felgenart *</label>
          <select 
            required 
            value={data.rim_type} 
            onChange={(e) => setData({ ...data, rim_type: e.target.value })} 
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green bg-white"
            data-testid="rim-type-select"
          >
            <option value="">Bitte wählen</option>
            <option value="Alufelgen">Alufelgen</option>
            <option value="Stahlfelgen">Stahlfelgen</option>
          </select>
        </div>
      </div>

      {/* Vorderachse */}
      <div className="bg-white rounded-xl p-4 mb-4 border border-gray-200">
        <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center">
          <span className="bg-brand-green text-white text-xs px-2 py-1 rounded mr-2">VA</span>
          Vorderachse
        </h4>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="relative">
            <label className="block text-xs font-medium text-gray-600 mb-1">Reifendimension *</label>
            <div className="relative">
              <input 
                type="text" 
                required 
                value={data.tire_dimension_front || dimensionSearch.front} 
                onChange={(e) => {
                  const val = e.target.value;
                  if (data.tire_dimension_front) {
                    setData({ ...data, tire_dimension_front: '' });
                  }
                  setDimensionSearch(prev => ({ ...prev, front: val }));
                  filterDimensions(val, 'front');
                  setShowDropdown(prev => ({ ...prev, front: true }));
                }}
                onFocus={() => {
                  if (dimensionSearch.front.length >= 2) {
                    setShowDropdown(prev => ({ ...prev, front: true }));
                  }
                }}
                onBlur={() => {
                  // Delay to allow click on dropdown item
                  setTimeout(() => {
                    setShowDropdown(prev => ({ ...prev, front: false }));
                    // If no selection was made but there's text, use the text as value
                    if (!data.tire_dimension_front && dimensionSearch.front) {
                      setData(prev => ({ ...prev, tire_dimension_front: dimensionSearch.front }));
                    }
                  }, 200);
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green text-sm" 
                placeholder="z.B. 205/55 R16"
                data-testid="tire-dimension-front-input"
              />
              <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
            {showDropdown.front && filteredDimensions.front.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredDimensions.front.map((dim, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => selectDimension(dim, 'front')}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-brand-green/10 border-b border-gray-100 last:border-0"
                  >
                    {dim}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Hersteller *</label>
            <div className="relative">
              <input
                type="text"
                list="tire-manufacturers-front"
                required
                value={data.tire_manufacturer_front}
                onChange={(e) => setData({ ...data, tire_manufacturer_front: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green text-sm bg-white"
                placeholder="Hersteller wählen oder eingeben"
                data-testid="tire-manufacturer-front-input"
              />
              <datalist id="tire-manufacturers-front">
                {tireData?.manufacturers?.map((m) => (
                  <option key={m.id} value={m.name}>{m.name}</option>
                ))}
              </datalist>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Profiltiefe (mm) *</label>
            <input 
              type="number" 
              step="0.1" 
              min="0"
              max="10"
              required 
              value={data.tire_profile_front_mm} 
              onChange={(e) => setData({ ...data, tire_profile_front_mm: e.target.value })} 
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green text-sm" 
              placeholder="5.5"
              data-testid="tire-profile-front-input"
            />
          </div>
        </div>
      </div>

      {/* Hinterachse */}
      <div className="bg-white rounded-xl p-4 border border-gray-200">
        <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center">
          <span className="bg-gray-600 text-white text-xs px-2 py-1 rounded mr-2">HA</span>
          Hinterachse
        </h4>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="relative">
            <label className="block text-xs font-medium text-gray-600 mb-1">Reifendimension *</label>
            <div className="relative">
              <input 
                type="text" 
                required 
                value={data.tire_dimension_rear || dimensionSearch.rear} 
                onChange={(e) => {
                  const val = e.target.value;
                  if (data.tire_dimension_rear) {
                    setData({ ...data, tire_dimension_rear: '' });
                  }
                  setDimensionSearch(prev => ({ ...prev, rear: val }));
                  filterDimensions(val, 'rear');
                  setShowDropdown(prev => ({ ...prev, rear: true }));
                }}
                onFocus={() => {
                  if (dimensionSearch.rear.length >= 2) {
                    setShowDropdown(prev => ({ ...prev, rear: true }));
                  }
                }}
                onBlur={() => {
                  // Delay to allow click on dropdown item
                  setTimeout(() => {
                    setShowDropdown(prev => ({ ...prev, rear: false }));
                    // If no selection was made but there's text, use the text as value
                    if (!data.tire_dimension_rear && dimensionSearch.rear) {
                      setData(prev => ({ ...prev, tire_dimension_rear: dimensionSearch.rear }));
                    }
                  }, 200);
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green text-sm" 
                placeholder="z.B. 205/55 R16"
                data-testid="tire-dimension-rear-input"
              />
              <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
            {showDropdown.rear && filteredDimensions.rear.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredDimensions.rear.map((dim, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => selectDimension(dim, 'rear')}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-brand-green/10 border-b border-gray-100 last:border-0"
                  >
                    {dim}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Hersteller *</label>
            <div className="relative">
              <input
                type="text"
                list="tire-manufacturers-rear"
                required
                value={data.tire_manufacturer_rear}
                onChange={(e) => setData({ ...data, tire_manufacturer_rear: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green text-sm bg-white"
                placeholder="Hersteller wählen oder eingeben"
                data-testid="tire-manufacturer-rear-input"
              />
              <datalist id="tire-manufacturers-rear">
                {tireData?.manufacturers?.map((m) => (
                  <option key={m.id} value={m.name}>{m.name}</option>
                ))}
              </datalist>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Profiltiefe (mm) *</label>
            <input 
              type="number" 
              step="0.1" 
              min="0"
              max="10"
              required 
              value={data.tire_profile_rear_mm} 
              onChange={(e) => setData({ ...data, tire_profile_rear_mm: e.target.value })} 
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green text-sm" 
              placeholder="5.5"
              data-testid="tire-profile-rear-input"
            />
          </div>
        </div>
      </div>
      
      {/* Quick copy button */}
      {data.tire_dimension_front && data.tire_manufacturer_front && (
        <button
          type="button"
          onClick={() => setData({
            ...data,
            tire_dimension_rear: data.tire_dimension_front,
            tire_manufacturer_rear: data.tire_manufacturer_front
          })}
          className="mt-3 text-sm text-brand-green hover:underline"
        >
          ↓ Gleiche Reifen für Hinterachse übernehmen
        </button>
      )}
    </div>

    {/* Aktueller Schaden durch Unfall */}
    <div className="bg-red-50 rounded-2xl p-6 border border-red-200">
      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
        <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
        Aktueller Schaden durch Unfall
      </h3>
      <p className="text-sm text-gray-600 mb-4">Beschreiben Sie den Schaden, der durch den aktuellen Unfall entstanden ist.</p>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">Beschreibung des aktuellen Schadens *</label>
          <textarea required value={data.damage_description} onChange={(e) => setData({ ...data, damage_description: e.target.value })} className="w-full px-4 py-3 border border-gray-200 bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400" rows="4" placeholder="z.B. Stoßfänger vorne eingedrückt, Motorhaube verzogen, Scheinwerfer defekt..." data-testid="damage-description-input"></textarea>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Schadenbereich *</label>
            <div className="grid grid-cols-2 gap-2">
              {['Front', 'Heck', 'Links', 'Rechts', 'Dach', 'Unterboden', 'Mehrere'].map((zone) => (
                <button
                  key={zone}
                  type="button"
                  onClick={() => setData({ ...data, damage_zone: zone })}
                  className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                    data.damage_zone === zone 
                      ? 'bg-red-500 text-white border-red-500' 
                      : 'bg-white text-gray-700 border-gray-200 hover:border-red-300'
                  }`}
                  data-testid={`damage-zone-${zone.toLowerCase()}-btn`}
                >
                  {zone}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Fahrbereit *</label>
            <select required value={data.vehicle_drivable === null ? '' : data.vehicle_drivable.toString()} onChange={(e) => setData({ ...data, vehicle_drivable: e.target.value === 'true' })} className="w-full px-4 py-3 border border-gray-200 bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400" data-testid="vehicle-drivable-select">
              <option value="">Bitte wählen</option>
              <option value="true">Ja - Fahrzeug ist fahrbereit</option>
              <option value="false">Nein - Fahrzeug ist nicht fahrbereit</option>
            </select>
          </div>
        </div>
      </div>
    </div>
    
    {/* Vorschäden - VOR dem Unfall */}
    <div className="bg-yellow-50 rounded-2xl p-6 border border-yellow-200">
      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
        <AlertCircle className="w-5 h-5 mr-2 text-yellow-600" />
        Vorschäden (vor dem Unfall)
      </h3>
      <p className="text-sm text-gray-600 mb-4">Gab es bereits Schäden am Fahrzeug BEVOR der aktuelle Unfall passiert ist?</p>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">Hatte das Fahrzeug Vorschäden? *</label>
          <select required value={data.pre_damage} onChange={(e) => setData({ ...data, pre_damage: e.target.value })} className="w-full px-4 py-3 border border-gray-200 bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400" data-testid="pre-damage-select">
            <option value="">Bitte wählen</option>
            <option value="Nein">Nein - Keine Vorschäden bekannt</option>
            <option value="Ja">Ja - Es gab bereits Vorschäden</option>
            <option value="Unbekannt">Unbekannt</option>
          </select>
        </div>
        
        {data.pre_damage === 'Ja' && (
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Beschreibung der Vorschäden *</label>
            <textarea required value={data.pre_damage_description} onChange={(e) => setData({ ...data, pre_damage_description: e.target.value })} className="w-full px-4 py-3 border border-gray-200 bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400" rows="3" placeholder="z.B. Kratzer an der Beifahrertür, Delle am Kotflügel hinten..." data-testid="pre-damage-description-input"></textarea>
          </div>
        )}
      </div>
    </div>

    <div className="flex justify-between pt-6">
      <Button onClick={onBack} variant="outline" className="border-2 border-gray-300" data-testid="step2-back-btn">
        <ArrowLeft className="w-5 h-5 mr-2" />
        Zurück
      </Button>
      <Button onClick={onNext} disabled={loading} size="lg" className="bg-brand-green hover:bg-brand-green-dark text-white px-8 py-4 rounded-xl font-semibold" data-testid="step2-next-btn">
        {loading ? 'Speichern...' : 'Weiter zu Dokumenten'}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  </div>
  );
};

// Step 3: Legal Documents - Anwalt, Abtretungserklärung, Vollmacht
const Step3Content = ({ data, setData, step1Data, onNext, onBack, loading }) => {
  const abtretungSignatureRef = React.useRef(null);
  const vollmachtSignatureRef = React.useRef(null);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Rechtliche Dokumente</h2>
        <p className="text-sm text-gray-500 mb-2">Anwalt-Auswahl, Abtretungserklärung und Vollmacht</p>
        <p className="text-xs text-brand-green font-medium">⏱ Dauer: ca. 3 Minuten</p>
      </div>

      {/* Anwalt-Auswahl */}
      <div className="bg-purple-50 rounded-2xl p-6 border border-purple-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
          <Scale className="w-5 h-5 mr-2 text-purple-600" />
          Anwaltliche Vertretung
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Möchten Sie einen Anwalt mit der Schadensregulierung beauftragen?
        </p>
        
        <div className="space-y-3">
          <label className={`flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all ${
            data.lawyer_option === 'none' ? 'bg-white border-purple-500 ring-2 ring-purple-200' : 'bg-white border-gray-200 hover:border-purple-300'
          }`}>
            <input 
              type="radio" 
              name="lawyer_option" 
              value="none"
              checked={data.lawyer_option === 'none'}
              onChange={(e) => setData({ ...data, lawyer_option: e.target.value })}
              className="mt-1 mr-3"
              data-testid="lawyer-option-none"
            />
            <div>
              <span className="font-semibold text-gray-900">Kein Anwalt benötigt</span>
              <p className="text-sm text-gray-500">Wir regeln den Schaden direkt mit der Versicherung</p>
            </div>
          </label>
          
          <label className={`flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all ${
            data.lawyer_option === 'own' ? 'bg-white border-purple-500 ring-2 ring-purple-200' : 'bg-white border-gray-200 hover:border-purple-300'
          }`}>
            <input 
              type="radio" 
              name="lawyer_option" 
              value="own"
              checked={data.lawyer_option === 'own'}
              onChange={(e) => setData({ ...data, lawyer_option: e.target.value })}
              className="mt-1 mr-3"
              data-testid="lawyer-option-own"
            />
            <div>
              <span className="font-semibold text-gray-900">Eigener Anwalt</span>
              <p className="text-sm text-gray-500">Ich habe bereits einen Anwalt</p>
            </div>
          </label>
          
          <label className={`flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all ${
            data.lawyer_option === 'cooperating' ? 'bg-white border-purple-500 ring-2 ring-purple-200' : 'bg-white border-gray-200 hover:border-purple-300'
          }`}>
            <input 
              type="radio" 
              name="lawyer_option" 
              value="cooperating"
              checked={data.lawyer_option === 'cooperating'}
              onChange={(e) => setData({ ...data, lawyer_option: e.target.value })}
              className="mt-1 mr-3"
              data-testid="lawyer-option-cooperating"
            />
            <div>
              <span className="font-semibold text-gray-900">Kooperierender Anwalt gewünscht</span>
              <p className="text-sm text-gray-500">Bitte weisen Sie mir einen Anwalt aus Ihrem Netzwerk zu</p>
            </div>
          </label>
        </div>
        
        {/* Eigener Anwalt - Felder */}
        {data.lawyer_option === 'own' && (
          <div className="mt-6 bg-white rounded-xl p-4 border border-purple-200">
            <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center">
              <Building2 className="w-4 h-4 mr-2 text-purple-600" />
              Anwaltsdaten
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name des Anwalts *</label>
                <input 
                  type="text" 
                  required
                  value={data.own_lawyer_name} 
                  onChange={(e) => setData({ ...data, own_lawyer_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="z.B. RA Max Mustermann"
                  data-testid="own-lawyer-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kanzlei <span className="text-gray-400">(optional)</span></label>
                <input 
                  type="text" 
                  value={data.own_lawyer_kanzlei} 
                  onChange={(e) => setData({ ...data, own_lawyer_kanzlei: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="Kanzleiname"
                  data-testid="own-lawyer-kanzlei-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefon *</label>
                <input 
                  type="tel" 
                  required
                  value={data.own_lawyer_phone} 
                  onChange={(e) => setData({ ...data, own_lawyer_phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="z.B. 040 12345678"
                  data-testid="own-lawyer-phone-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail *</label>
                <input 
                  type="email" 
                  required
                  value={data.own_lawyer_email} 
                  onChange={(e) => setData({ ...data, own_lawyer_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="anwalt@kanzlei.de"
                  data-testid="own-lawyer-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Straße, Nr <span className="text-gray-400">(optional)</span></label>
                <input 
                  type="text" 
                  value={data.own_lawyer_strasse} 
                  onChange={(e) => setData({ ...data, own_lawyer_strasse: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="Musterstraße 1"
                  data-testid="own-lawyer-strasse-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PLZ</label>
                  <input 
                    type="text" 
                    value={data.own_lawyer_plz} 
                    onChange={(e) => setData({ ...data, own_lawyer_plz: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                    placeholder="20095"
                    data-testid="own-lawyer-plz-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ort</label>
                  <input 
                    type="text" 
                    value={data.own_lawyer_ort} 
                    onChange={(e) => setData({ ...data, own_lawyer_ort: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                    placeholder="Hamburg"
                    data-testid="own-lawyer-ort-input"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Abtretungserklärung */}
      <div className="bg-green-50 rounded-2xl p-6 border border-green-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2 text-green-600" />
          Abtretungserklärung
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Mit der Abtretungserklärung übertragen Sie Ihren Anspruch auf Erstattung der Gutachterkosten an uns. 
          Wir rechnen direkt mit der gegnerischen Versicherung ab.
        </p>
        
        {/* Summary of data that will be in PDF */}
        <div className="bg-white rounded-xl p-4 mb-4 text-sm">
          <p className="font-medium text-gray-700 mb-2">Folgende Daten werden übernommen:</p>
          <div className="grid md:grid-cols-2 gap-2 text-gray-600">
            <p><span className="font-medium">Anspruchsteller:</span> {step1Data.victim_name || '–'}</p>
            <p><span className="font-medium">Kennzeichen:</span> {step1Data.license_plate || '–'}</p>
            <p><span className="font-medium">Schadentag:</span> {step1Data.damage_date || '–'}</p>
            <p><span className="font-medium">Schädiger:</span> {step1Data.schaediger_name || '–'}</p>
          </div>
        </div>
        
        {/* Checkboxes */}
        <div className="space-y-3 mb-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input 
              type="checkbox" 
              checked={data.abtretung_accept_agb}
              onChange={(e) => setData({ ...data, abtretung_accept_agb: e.target.checked })}
              className="mt-1 w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
              data-testid="abtretung-agb-checkbox"
            />
            <span className="text-sm text-gray-700">
              Ich akzeptiere die <a href="/agb" target="_blank" className="text-green-600 underline">Datenschutzbestimmungen</a> und die <a href="/agb" target="_blank" className="text-green-600 underline">AGB</a> von dein-gutachten-direkt.de. *
            </span>
          </label>
          
          <label className="flex items-start gap-3 cursor-pointer">
            <input 
              type="checkbox" 
              checked={data.abtretung_accept_widerruf}
              onChange={(e) => setData({ ...data, abtretung_accept_widerruf: e.target.checked })}
              className="mt-1 w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
              data-testid="abtretung-widerruf-checkbox"
            />
            <span className="text-sm text-gray-700">
              Ich bestätige, dass ich die Widerrufsbelehrung erhalten habe. *
            </span>
          </label>
          
          <label className="flex items-start gap-3 cursor-pointer">
            <input 
              type="checkbox" 
              checked={data.abtretung_accept_sofort}
              onChange={(e) => setData({ ...data, abtretung_accept_sofort: e.target.checked })}
              className="mt-1 w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
              data-testid="abtretung-sofort-checkbox"
            />
            <span className="text-sm text-gray-700">
              Ich bin einverstanden und verlange ausdrücklich, dass vor Ende der Widerrufsfrist mit der Ausführung der beauftragten Dienstleistung begonnen wird. Mir ist bekannt, dass ich bei vollständiger Vertragserfüllung mein Widerrufsrecht verliere. *
            </span>
          </label>
        </div>
        
        {/* Signature */}
        <SignaturePad 
          ref={abtretungSignatureRef}
          label="Unterschrift Auftraggeber"
          required
          onChange={(sig) => setData({ ...data, abtretung_signature: sig })}
        />
      </div>

      {/* Vollmacht - nur wenn Anwalt gewünscht */}
      {data.lawyer_option && data.lawyer_option !== 'none' && (
        <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <Scale className="w-5 h-5 mr-2 text-blue-600" />
            Vollmacht für anwaltliche Vertretung
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {data.lawyer_option === 'own' 
              ? `Die Vollmacht wird für Ihren Anwalt ${data.own_lawyer_name || ''} ausgestellt.`
              : 'Die Vollmacht wird für den von uns zugewiesenen kooperierenden Anwalt ausgestellt.'
            }
          </p>
          
          {/* Summary */}
          <div className="bg-white rounded-xl p-4 mb-4 text-sm">
            <p className="font-medium text-gray-700 mb-2">Vollmacht in der Sache:</p>
            <p className="text-gray-600">
              {step1Data.victim_name || '–'} ./. {step1Data.schaediger_name || 'Schädiger'} - Verkehrsunfall vom {step1Data.damage_date || '–'}
            </p>
          </div>
          
          {/* DSGVO Checkbox */}
          <div className="mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input 
                type="checkbox" 
                checked={data.vollmacht_accept_dsgvo}
                onChange={(e) => setData({ ...data, vollmacht_accept_dsgvo: e.target.checked })}
                className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                data-testid="vollmacht-dsgvo-checkbox"
              />
              <span className="text-sm text-gray-700">
                Ich wurde auf einem gesonderten Blatt auf die Datenschutzhinweise (DSGVO) hingewiesen und bin damit einverstanden. *
              </span>
            </label>
          </div>
          
          {/* Signature */}
          <SignaturePad 
            ref={vollmachtSignatureRef}
            label="Unterschrift Vollmachtgeber"
            required
            onChange={(sig) => setData({ ...data, vollmacht_signature: sig })}
          />
        </div>
      )}

      <div className="flex justify-between pt-6">
        <Button onClick={onBack} variant="outline" className="border-2 border-gray-300" data-testid="step3-back-btn">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Zurück
        </Button>
        <Button onClick={onNext} disabled={loading} size="lg" className="bg-brand-green hover:bg-brand-green-dark text-white px-8 py-4 rounded-xl font-semibold" data-testid="step3-next-btn">
          {loading ? 'Dokumente werden erstellt...' : 'Weiter zu Bildern'}
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default NeuerFallPage;
